/**
 * NFR-9 — the one place configuration lives.
 *
 * Every default, threshold and cap in Touchline is declared here and validated at import time by
 * `./validate.ts`. There are no magic numbers at call sites. Adding a number to a call site is a
 * review failure, not a style preference: the four gesture thresholds and `REORDER_QUIET_MS` are
 * expected to move after first field use (VER-3 item 4c), and they can only move as a config edit
 * if they never leaked.
 *
 * Section 1 is the complete design-spec §1.3 inventory, reproduced including the values the design
 * set. It is present in full even though build units U-0 and U-1 use only a fraction of it —
 * NFR-9 says config lives in one module, and splitting it "when the rest is built" is how the
 * discipline erodes.
 *
 * Section 2 is constants U-0 introduces, section 3 constants U-1 introduces. Each names its source
 * requirement. See the note on `LOG_RING_CAP` — §1.3 claims to be complete and is not.
 *
 * BUILD-UNIT NOTE (U-2): design §1.3 is authoritative for **names** as well as values. Every name
 * here now matches it, and `spec13.test.ts` diffs the two in both directions on every test run —
 * a §1.3 row with no constant, and a constant whose value disagrees, both fail the build. That
 * check is what §1.3 itself says is the only thing that closes the gap; it is no longer a comment.
 *
 * BUILD-UNIT NOTE (U-1): section 1 previously stopped at `SWIPE_MIN_PX` while claiming to be the
 * complete §1.3 set — sixteen declared rows were missing, including `PLAYABILITY_PROBE_TIMEOUT_MS`,
 * which U-1 needs. A completeness claim is what makes a later reader stop looking, so the claim has
 * to be earned; the missing rows are added below. Reported to the orchestrator.
 */

// ─────────────────────────────────────────────────────────────────────────────
// 1. Design spec §1.3 — the complete v1 inventory
// ─────────────────────────────────────────────────────────────────────────────

/** NFR-9 — count at which a hypothesis auto-confirms (INV-3). */
export const PATTERN_THRESHOLD = 3;

/** NFR-9 — maximum hypotheses holding a `boardPosition` per match (INV-5). */
export const BOARD_CAP = 8;

/** NFR-9 — required count of `phase=fullTime` TalkPoints on an archived match (INV-6). */
export const TALKPOINT_COUNT = 3;

/** NFR-9 — voice-note auto-stop (AUD-2), seconds. */
export const VOICE_NOTE_MAX_S = 60;

/** NFR-9 — how long a `−1` affordance stays live after an increment (LIVE-6), seconds. */
export const UNDO_WINDOW_S = 8;

/** NFR-9 — match minute from which the "Lock My 8" affordance appears (LOCK-1). User-configurable per BAK-5. */
export const LOCK_NUDGE_MIN = 15;

/** NFR-9 — minutes past `halfLengthMin` before the "end the half?" nudge (SES-7). */
export const HALF_OVERRUN_NUDGE_MIN = 10;

/** NFR-9, value set by design §5.5 / §15 (DD-1) — no row may change rendered position within this window of any tap (DISC-7). */
export const REORDER_QUIET_MS = 1000;

/** NFR-9, value set by design §2.2 (SES-1) — sustained press required by the four clock-anchoring transitions. */
export const TRANSITION_HOLD_MS = 700;

/** SES-5 — default half length in minutes. */
export const HALF_LENGTH_DEFAULT = 35;

/** SES-5 — inclusive lower bound on `Match.halfLengthMin`. */
export const HALF_LENGTH_MIN_MIN = 20;

/** SES-5 — inclusive upper bound on `Match.halfLengthMin`. */
export const HALF_LENGTH_MIN_MAX = 45;

/**
 * Design §1.3 / §5.3 — a **logged measurement threshold**, never a UI branch.
 *
 * This is the number build unit U-0 exists to test the design's tap path against. A commit slower
 * than this is not an error and produces no user-visible state; it is counted into the p95 that
 * §5.1 writes into the match archive.
 */
export const ACK_COMMIT_BUDGET_MS = 60;

/** Design §1.3 / §8.5 — `MediaRecorder` chunk interval; bounds worst-case audio loss on hard jetsam. */
export const TIMESLICE_MS = 1000;

/** Design §1.3 / §7.3 — bounds the HT-5 halftime adjustment candidate list. */
export const HT_ADJUSTMENT_CANDIDATES = 8;

/** Design §1.3 / §5.5 (DD-1) — press duration that classifies as a long-press. */
export const LONG_PRESS_MS = 450;

/** Design §1.3 / §5.5 (DD-1) — movement above this cancels a tap or long-press classification. */
export const TAP_SLOP_PX = 10;

/** Design §1.3 / §5.5 (DD-1) — minimum travel that classifies as a swipe. */
export const SWIPE_MIN_PX = 44;

/** NFR-2 — cold launch to interactive, milliseconds. */
export const COLD_LAUNCH_BUDGET_MS = 2000;

/** NFR-3, DISC-3 — minimum touch target, points. */
export const MIN_TOUCH_TARGET_PT = 60;

/** LIVE-1 — minimum Flag bar height, points. */
export const FLAG_BAR_MIN_PT = 80;

/** NFR-4 — VER-3 checklist threshold: storage after a three-game day, megabytes. */
export const STORAGE_BUDGET_3MATCH_MB = 100;

/** PRE-2 — minimum seeded hypotheses per moment bucket; enforced by the CI seed validation. */
export const MIN_SEEDS_PER_BUCKET = 10;

/** OBS-2 — taps on the version string that open the debug screen. */
export const DEBUG_TAP_COUNT = 5;

/** LIVE-2 — the count flash scale factor ("~2×"). */
export const ACK_FLASH_SCALE = 2;

/**
 * NFR-1 — tap-to-visual-ack budget in milliseconds, measured on the target device.
 *
 * The assertion threshold for build unit U-0 and for VER-1. A run's verdict is stated against this,
 * and against A-1's tighter ~40 ms p95 claim (`A1_ASSUMED_WARM_P95_MS`).
 */
export const TAP_ACK_BUDGET_MS = 100;

/** LIVE-2 — the acknowledgment persists at least this long once begun. */
export const ACK_PERSIST_MIN_MS = 300;

/** Design §1.3 / §2.2 — tolerance on `anchorTs` validation, covering dispatch and event-loop latency. */
export const HOLD_SLOP_MS = 250;

/** Design §1.3 / §5.5 — FLIP settle duration for the DISC-7 deferred reorder. */
export const REORDER_ANIM_MS = 200;

/** Design §1.3 / §6.4 — in-tile `−1` hit region inset, points. */
export const UNDO_INSET_PT = 44;

/** Design §1.3 / §7.3 — minimum type size for the read-aloud HT-5 adjustment list, points. */
export const ADJUSTMENT_TEXT_PT = 28;

/** Design §1.3 / §11.2 — manifest `GET` timeout. */
export const MANIFEST_TIMEOUT_MS = 30_000;

/** Design §1.3 / §11.2 — archive `PUT` timeout. */
export const ARCHIVE_PUT_TIMEOUT_MS = 120_000;

/** Design §1.3 / §15 DD-4 — the backup service's 413 threshold, megabytes. */
export const MAX_ARCHIVE_MB = 64;

/**
 * Design §1.3 / §8.2 — how long the playability probe waits before treating a segment as unplayable.
 *
 * §13.1 pairs it with "no retry — treat as unplayable". Build unit U-1 is the first consumer: it is
 * the bound on the `HTMLAudioElement` probe that decides whether a concatenated chunk sequence is
 * playable at all, which is the crux of assumption A-2.
 */
export const PLAYABILITY_PROBE_TIMEOUT_MS = 3000;

/** Design §1.3 / §13.7 — heartbeat age past which a writer-lock holder is considered gone. */
export const WRITER_LOCK_STALE_MS = 90_000;

/**
 * OBS-1 — "a capped ring buffer of ~2,000 entries in IndexedDB in production".
 *
 * Absent from the first version of design §1.3 and found by build unit U-0, which reported it; §1.3
 * now carries the row and credits the finding. Left uncorrected, the first logger implementation
 * writes `2000` at a call site, which is exactly what NFR-9 forbids.
 */
export const LOG_RING_CAP = 2000;

/** Design §1.3 / §12.1 — ring trimming is amortized: trim on every Nth flush, not every flush. */
export const LOG_TRIM_EVERY_N_FLUSHES = 256;

/**
 * Design §1.3 / §13 — warn when `storage.estimate()` usage crosses this fraction of quota (BAK-3,
 * NFR-4).
 *
 * A fraction, not a percent. The unit is in the identifier because the two forms differ by 100x and
 * nothing at the call site would catch it.
 */
export const STORAGE_WARN_FRACTION = 0.8;

// ─────────────────────────────────────────────────────────────────────────────
// 2. Introduced by build unit U-0
// ─────────────────────────────────────────────────────────────────────────────

/** OBS-1 / design §12.1 — flush the in-memory log buffer once it reaches this many entries. */
export const LOG_FLUSH_THRESHOLD = 32;

/** Design §0.1 A-1 — the p95 the design assumes for a warm two-row `readwrite`. */
export const A1_ASSUMED_WARM_P95_MS = 40;

/** U-0 — trials discarded before measurement begins, so JIT warm-up is not counted as a commit cost. */
export const HARNESS_WARMUP_TRIALS = 20;

/** U-0 — default measured trials per condition. Large enough that a p99 means something. */
export const HARNESS_TRIALS_DEFAULT = 200;

/** U-0 — lower bound on measured trials. Below this a p99 is noise and the verdict says so. */
export const HARNESS_TRIALS_MIN = 50;

/** U-0 — upper bound on measured trials, so a mistyped value cannot lock the phone up at a venue. */
export const HARNESS_TRIALS_MAX = 2000;

/** U-0 — sample count below which a p99 is reported but explicitly not trusted. */
export const HARNESS_P99_MIN_SAMPLES = 100;

/**
 * U-0 — hard cap on a live-tap session, so a pad left face-up in a pocket cannot grow a run without
 * bound. It is a safety stop, **not a target**: the pad is open-ended and the operator decides when
 * to finish.
 *
 * There is deliberately no separate "live-tap target" constant. A first device run shipped one — a
 * literal `30` in the harness screen — while admissibility is gated on `HARNESS_TRIALS_MIN = 50`,
 * so the live-tap mode was structurally incapable of ever returning anything but `inconclusive`,
 * on the one measurement that carries real touch input. Two independent numbers that must agree
 * will eventually disagree. The pad now reads the floor itself, and the only remaining bound is
 * this ceiling, which `validate.ts` refuses to let fall below the floor.
 */
export const HARNESS_LIVE_TAP_MAX = 500;

/**
 * U-0 — how long to wait for an `IDBTransaction` `complete` event before calling a trial failed.
 *
 * A stall detector, not a threshold: two orders of magnitude above NFR-1's budget, so anything
 * approaching it is already a catastrophic result the report will show on its own. It exists so a
 * transaction that never commits becomes a recorded failure rather than a harness that hangs at a
 * venue with a progress bar that never moves.
 */
export const HARNESS_COMMIT_EVENT_TIMEOUT_MS = 5000;

/**
 * U-0 — same role for the animation frame. iOS stops delivering frames to a backgrounded page, so
 * without this bound, switching apps mid-run would leave the harness waiting forever.
 */
export const HARNESS_FRAME_TIMEOUT_MS = 5000;

/**
 * U-0 — gap between trials in "realistic" pacing mode, milliseconds.
 *
 * Two pacings are measured because they stress different things. Burst pacing (frame-paced, ~16 ms
 * apart) keeps the IndexedDB connection hot and exercises design §5.2's single-flight queue.
 * Realistic pacing lets the connection idle between taps, which is what actually happens on the
 * Discovery pages, and is where a re-warm cost would show up.
 */
export const HARNESS_PACING_REALISTIC_MS = 250;

/** U-0 — default thermal soak duration before the loaded condition measures, in seconds. */
export const HARNESS_THERMAL_SOAK_S = 120;

/** U-0 — cap on soak duration, so the harness cannot be told to cook the phone indefinitely. */
export const HARNESS_THERMAL_SOAK_MAX_S = 900;

/** U-0 — heat workers spawned for the thermally-loaded condition, capped regardless of core count. */
export const HARNESS_HEAT_WORKERS_MAX = 6;

/** A-6 — matches a season holds; the default seed size models a mid-season database. */
export const SEED_MATCHES_DEFAULT = 20;

/** A-6 — "a match produces 100–600 increments"; the midpoint is the default per-match seed. */
export const SEED_OBSERVATIONS_PER_MATCH_DEFAULT = 400;

/** A-6 — hypotheses per seeded match: `BOARD_CAP` positioned plus a realistic list-only tail. */
export const SEED_HYPOTHESES_PER_MATCH = 28;

/** U-0 — rows written per seeding transaction, so a large seed does not hold one transaction open for minutes. */
export const SEED_CHUNK_ROWS = 500;

/** U-0 — size of one synthetic audio blob when bulk-seeding storage, in bytes (≈120 KB, a ~10 s AAC segment). */
export const SEED_AUDIO_BLOB_BYTES = 120_000;

/** U-0 — retained run records; a run is a few KB, and a venue session produces a handful. */
export const HARNESS_RUN_HISTORY_CAP = 50;

// ─────────────────────────────────────────────────────────────────────────────
// 3. Introduced by build unit U-1 — the MediaRecorder spike (assumption A-2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AUD-1 — the MIME types the capability probe asks `MediaRecorder.isTypeSupported` about.
 *
 * Data, not logic: AUD-1 forbids transcoding on any path, so the product must store whatever the
 * recorder emits. This list exists to find out **what that is**, not to pick a favourite. iOS
 * Safari is expected to answer only for the `audio/mp4` family and Chromium only for the WebM/Opus
 * family — which is precisely why a desktop run cannot settle A-2.
 */
export const AUDIO_MIME_CANDIDATES: readonly string[] = [
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/aac',
  'audio/webm',
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/mpeg',
  'audio/wav',
];

/**
 * U-1 — default length of one spike segment, seconds.
 *
 * Long enough that `AUDIO_SPIKE_MIN_CHUNKS` timeslices arrive with margin, so the sequence being
 * concatenated is a real sequence rather than one chunk plus a stub. Short enough that several
 * segments fit inside a halftime break.
 */
export const AUDIO_SPIKE_SEGMENT_S = 12;

/** U-1 — floor on a spike segment. Below this the chunk sequence is too short to say anything. */
export const AUDIO_SPIKE_SEGMENT_MIN_S = 4;

/**
 * U-1 — chunks below which a segment cannot settle A-2.
 *
 * A-2 claims "a timesliced chunk sequence concatenated verbatim (init fragment first, chunks in
 * order) is playable". A two-chunk segment cannot distinguish "concatenation works" from "the
 * recorder happened to emit one usable file", so such a segment is reported and excluded from the
 * verdict rather than counted as a pass.
 */
export const AUDIO_SPIKE_MIN_CHUNKS = 4;

/**
 * U-1 — complete segments required before the A-2 verdict may read anything but `inconclusive`.
 *
 * `empirical-absence-not-invariant`: one playable assembly is an anecdote. Three independent
 * segments is still small, but it is the difference between "it worked" and "it works".
 */
export const AUDIO_SPIKE_MIN_SEGMENTS = 3;

/** U-1 — `getUserMedia` acquisition trials per pass. */
export const GUM_TRIALS_DEFAULT = 5;

/** U-1 — lower bound on acquisition trials; below this the spread is not a spread. */
export const GUM_TRIALS_MIN = 3;

/** U-1 — upper bound, so a mistyped value cannot cycle the microphone a hundred times at a venue. */
export const GUM_TRIALS_MAX = 25;

/**
 * Design §8.1 — the upper end of the design's own stated cost for `getUserMedia` on iOS
 * ("200–800 ms"), and therefore the threshold that falsifies it.
 *
 * §8.1 rejects per-recording acquisition on exactly this number, so it is a claim U-1 must check
 * rather than inherit. The *warm* path is checked against `TAP_ACK_BUDGET_MS` instead — §8.1 says
 * acquisition "costs nothing" once the stream is held, and FLAG-1's "starts recording immediately"
 * is the same perceptual promise NFR-1 already quantifies.
 */
export const GUM_COLD_ASSUMED_MAX_MS = 800;

/**
 * U-1 — tolerance on the observed timeslice cadence, as a fraction of `TIMESLICE_MS`.
 *
 * The design depends on the cadence only through §8.5's "worst-case loss is one timeslice", so what
 * has to hold is that the interval is *bounded and close to what was asked for*, not that it is
 * exact. A median inside ±50 % keeps §8.5's claim honest.
 */
export const TIMESLICE_TOLERANCE_FRACTION = 0.5;

/**
 * U-1 — multiple of `TIMESLICE_MS` the **worst** observed inter-chunk interval may reach.
 *
 * If a single gap runs to twice the timeslice, §8.5's "at most one `TIMESLICE_MS` lost" understates
 * the loss on a hard jetsam kill and the sentence needs correcting.
 */
export const TIMESLICE_MAX_INTERVAL_FACTOR = 2;

/** U-1 — retained spike runs. A run carries stats, not audio, so it is a few KB. */
export const AUDIO_RUN_HISTORY_CAP = 25;

/**
 * U-1 — the pretend match minute a spike segment is stamped with by default.
 *
 * 44′ is the boundary case design §8.2 names by name: *"a flag started at 44:50 and stopped after
 * the Half tap"*. The spike has no state machine, so this is the value that demonstrates SES-10's
 * ordering — the minute is derived when recording STARTS, not when it finalizes.
 */
export const AUDIO_SPIKE_DEFAULT_MATCH_MINUTE = 44;

/**
 * U-1 — upper bound on the pretend match minute input.
 *
 * Derived rather than picked: the largest minute the product's own clock can produce is two
 * maximum-length halves plus the overrun the SES-7 nudge tolerates.
 */
export const AUDIO_SPIKE_MAX_MATCH_MINUTE = HALF_LENGTH_MIN_MAX * 2 + HALF_OVERRUN_NUDGE_MIN;

/**
 * U-1 — cap on persisted spike segments before the oldest are swept.
 *
 * These *do* carry audio, and a venue session should not silently fill the phone. The sweep is
 * oldest-first and only ever touches segments the spike itself created.
 */
export const AUDIO_SEGMENT_HISTORY_CAP = 12;

// ─────────────────────────────────────────────────────────────────────────────
// 4. Identifiers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The harness writes to its OWN IndexedDB database, not the product's.
 *
 * Two reasons, both load-bearing. (1) The harness seeds and wipes thousands of rows; doing that to
 * the database that will later hold real scouting data is a data-loss path with no upside.
 * (2) U-3 owns the product's `v001` schema, and a harness that had already claimed a Dexie version
 * number on the product database would force U-3 to start at `v002` for no reason.
 */
export const HARNESS_DB_NAME = 'touchline-u0-harness';

/**
 * Forward-only numbered migrations from v001 (NFR-7). This is the harness DB's current version.
 *
 * v002 (build unit U-1) adds the audio-spike stores. v001 is never edited — a device that already
 * applied it will never apply it again, so an edit would produce two different v001 schemas in the
 * field.
 */
export const HARNESS_DB_VERSION = 2;

/**
 * The **product** database. Separate from the harness DB, and starting its own numbering at v001.
 *
 * NFR-7 requires "numbered forward-only Dexie migrations from v001" and a fixture DB from the first
 * shipped schema that survives every later migration. The harness already burned v001 and v002 on
 * `touchline-u0-harness`; extending that chain would mean the product's first shipped schema was
 * `v003` and the NFR-7 fixture would be a harness artifact. Its own database is what keeps the
 * product's migration story the one NFR-7 describes.
 */
export const PRODUCT_DB_NAME = 'touchline';

/**
 * The product schema's current Dexie version (NFR-7). Kept in step with
 * `data/migrations/product/index.ts`, which the schema test asserts in both directions.
 */
export const PRODUCT_DB_VERSION = 1;

// ─────────────────────────────────────────────────────────────────────────────
// 5. Introduced by build unit U-3 — schema, repos, BlobStore
// ─────────────────────────────────────────────────────────────────────────────

/**
 * U-3 / design §5.1 step 8 — how long a write waits for the native `IDBTransaction` `complete`
 * event before it is declared uncommittable.
 *
 * A **stall detector, not a threshold**: two orders of magnitude above NFR-1's `TAP_ACK_BUDGET_MS`,
 * so anything approaching it is already a catastrophic result. It exists because SES-2 forbids
 * acknowledging an uncommitted mutation — so a transaction that never reports `complete` must
 * become a surfaced, typed failure rather than a screen that never flashes and never explains why.
 */
export const COMMIT_EVENT_TIMEOUT_MS = 5000;

/**
 * U-3 / §11.7 — decimal places geometry is rounded to before it is stored.
 *
 * §11.7's determinism controls require "no floats in serialized geometry beyond 4 dp", because
 * float formatting varies between engines and one differing digit defeats INV-12's byte-identity.
 * Rounding at the **repo boundary**, not in the serializer, is what makes that achievable: U-5 can
 * only be byte-deterministic if the bytes it is handed already are. SP-5's markers are normalized
 * 0–1 box coordinates, so 4 dp is ~0.1 mm on a penalty-box diagram — far below what a fingertip
 * can express.
 */
export const GEOMETRY_DECIMALS = 4;

// ─────────────────────────────────────────────────────────────────────────────
// 6. Introduced by build unit U-5 — the archive format
// ─────────────────────────────────────────────────────────────────────────────

/**
 * U-5 / §11.7 — the `formatVersion` written into every `manifest.json`.
 *
 * The **only** version negotiation SYN-9 allows: *"A client-side schema migration MUST NOT require a
 * coordinated server deploy. Any archive the service holds remains retrievable and importable by a
 * later client version; version negotiation, where needed, happens client-side on import."* SYN-8
 * forbids the service from parsing the archive at all, so the version must travel inside the bytes
 * the service treats as opaque, and the importer must read it before anything else.
 *
 * Distinct from `PRODUCT_DB_VERSION`, and deliberately so: the Dexie version describes the shape of
 * the database on *this* device, the format version describes the shape of a file that outlives it.
 * A migration that adds a nullable column changes the first and not the second.
 */
export const ARCHIVE_FORMAT_VERSION = 1;
